/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import * as joint from '@joint/plus';
import * as canvasActions from './canvas-actions';
import * as canvasUtils from './node-utils';
import EventSequenceService from '../../services/event-sequence.service';
import {CanvasEnvironment} from './canvas-environment';
import {CalloutPresentation} from '../../@types/callout';
import {setCalloutOpenForm} from '../isam-nodes'

export abstract class Controller extends joint.mvc.Listener<[CanvasEnvironment]> {

    constructor(public readonly service: CanvasEnvironment) {
        super(service);
        this.startListening();
    }

    abstract startListening(): void;
}

export class CanvasController extends Controller {

    startListening() {
        const { paper } = this.service;
        this.listenTo(paper, {
            'element:contextmenu': (service: CanvasEnvironment, elementView: joint.dia.ElementView, evt: joint.dia.Event) => {
                evt.preventDefault();
                evt.stopPropagation();
                if (elementView.model.get('nodeClass') === 'CALLOUTNODE') return;
                if (elementView.model.get('nodeClass') === 'PSFNODE') return;
                if (elementView.model.get('nodeClass') === 'BARRIERCONTAINER') return;
                canvasActions.setSelection(service, [elementView.model]);
                service.contextMenuCallback(evt, elementView);
            },
            'element:pointerdown': (service, elementView, evt: joint.dia.Event) => {
                if (elementView.model.get('nodeClass') === 'CALLOUTNODE') return;
                if (elementView.model.get('nodeClass') === 'PSFNODE') return;
                if (elementView.model.get('nodeClass') === 'BARRIERCONTAINER') return;
                if (canvasActions.canEdit(service) && service.currentLinkedOccurrenceDna !== null) {
                    if (evt.shiftKey) {
                        let [previousSelection] = service.selection;
                        let path = canvasActions.getDNAPath(service.graph, previousSelection, elementView.model);
                        service.HighlighterFrame.remove(elementView, 'frame'); // remove from highlighter before drawing dna to avoid a strange highlighter behavior    
                        service.setDnaPath(path);
                    } else if (evt.altKey) {
                        service.clearLinkedOccurrenceDna();
                    }
                }

                canvasActions.setSelection(service, [elementView.model]);
            },
            'element:pointerup': (service, elementView) => {
                if (elementView.model.get('nodeClass') !== 'CALLOUTNODE') return;
                let [parent] = service.graph.getNeighbors(elementView.model, { inbound: true });
                
                if (parent !== undefined) {
                    let x_f = (elementView.model.position().x - parent.position().x) / parent.size().width;
                    let y_f = (elementView.model.position().y - parent.position().y) / parent.size().height;
                    
                    let callout = parent.get('data').callout;
                    if (callout) {
                        let calloutPresentation = JSON.parse(callout.presentation) as CalloutPresentation;
                        calloutPresentation.x_factor = x_f;
                        calloutPresentation.y_factor = y_f;
                        let updateCallout = {
                            id: parent.get('data').id,
                            uniqueId: parent.get('data').uniqueId,
                            name: parent.get('data').name,
                            callout: {
                                content: callout.content,
                                presentation: JSON.stringify(calloutPresentation)
                            }
                            
                        }
                        parent.get('data').callout.presentation = JSON.stringify(calloutPresentation);
                        return canvasUtils.isEvent(parent.get('data').type) ? EventSequenceService.updateEvent(updateCallout, false) : EventSequenceService.updateFaultTreeNode(updateCallout, false);
                    }
                }
                
                
            },
            'element:mouseenter': (service, elementView) => {
                if (elementView.model.get('nodeClass') === 'CALLOUTNODE') return;
                if (elementView.model.get('nodeClass') === 'PSFNODE') return;
                if (elementView.model.get('nodeClass') === 'BARRIERCONTAINER') return;

                service.HighlighterFrame.add(elementView, 'body', 'frame', { padding: 0 });
            },
            'element:mouseleave': (service, elementView) => {
                if (elementView.model.get('nodeClass') === 'CALLOUTNODE') return;
                if (elementView.model.get('nodeClass') === 'PSFNODE') return;
                if (elementView.model.get('nodeClass') === 'BARRIERCONTAINER') return;
                service.HighlighterFrame.remove(elementView, 'frame');
            },
            'blank:mousewheel': (service, evt, ox, oy, delta) => {
                const direction = delta < 0 ? -1 : 1;
                evt.preventDefault();
                evt.stopPropagation();
                service.paperScroller.zoom(direction * 0.01, { min: 0.01, max: 4, grid: 0.01, ox, oy });
            },
            'cell:mousewheel': (service, _cellView: joint.dia.CellView, evt, ox, oy, delta) => {
                const direction = delta < 0 ? -1 : 1;
                evt.preventDefault();
                evt.stopPropagation();
                service.paperScroller.zoom(direction * 0.01, { min: 0.01, max: 4, grid: 0.01, ox, oy });
            },
            'blank:pointerdown': (service, evt) => {
                service.paperScroller.startPanning(evt);    
                if (evt.altKey) {
                    service.clearLinkedOccurrenceDna();
                }
            },
            'element:expand': (service, elementView: joint.dia.ElementView, _evt: joint.dia.Event, buttonView: joint.elementTools.Button) => {
                const element = elementView.model;
                const nodeClass = element.get('nodeClass');
                if (nodeClass === 'CALLOUTNODE') return;
                if (nodeClass === 'PSFNODE') return;
                let { activeTopNode, graph, paper, paperScroller } = service;

                // Handle BarrierContainer expand/collapse - toggle embedded barriers visibility
                if (nodeClass === 'BARRIERCONTAINER') {
                    let viewportOverlap = 50;
                    let viewportRect: any = paperScroller.getVisibleArea().inflate(viewportOverlap);
                    canvasActions.toggleBranch(service, element, graph, paper, paperScroller, viewportRect);
                    service.drawDnaPath();
                    return;
                }

                if (nodeClass !== 'FAULTTREENODE') {
                    // collapse the previous ESD event fault tree branch if it is expanded
                    // Check if there are FT nodes in the graph (including BarrierContainers)
                    const ftElementsInGraph = graph.getElements().filter(cell => {
                        const cellNodeClass = cell.get('nodeClass');
                        const cellId = cell.id as string;
                        // Check by nodeClass OR by ID pattern (barrier containers have 'barrier-container-' prefix)
                        return cellNodeClass === 'FAULTTREENODE' ||
                               cellNodeClass === 'BARRIERCONTAINER' ||
                               cellId?.startsWith('barrier-container-');
                    });

                    if (ftElementsInGraph.length > 0) {
                        // Increment generation to invalidate any pending async callbacks from expand
                        service.expansionGeneration++;

                        // Use same pattern as toggleBranch for consistent behavior
                        paper.freeze();

                        // Remove all FT elements - cells without proper graph reference can't be removed normally
                        // So we use resetCells to keep only the cells we want
                        const ftElementIds = new Set(ftElementsInGraph.map(cell => cell.id));

                        // Also remove links connected to FT elements
                        const linksToRemove = graph.getLinks().filter(link => {
                            const sourceId = link.get('source')?.id;
                            const targetId = link.get('target')?.id;
                            return ftElementIds.has(sourceId) || ftElementIds.has(targetId);
                        });
                        const linkIdsToRemove = new Set(linksToRemove.map(link => link.id));

                        // Keep cells that are not FT elements and not links to FT elements
                        const cellsToKeep = graph.getCells().filter(cell => {
                            return !ftElementIds.has(cell.id) && !linkIdsToRemove.has(cell.id);
                        });

                        // Reset graph with only the cells we want to keep
                        graph.resetCells(cellsToKeep);

                        canvasActions.setSelection(service, []);
                        if (activeTopNode) {
                            activeTopNode.set('collapsed', true);
                        }

                        // Force update (same trick as toggleBranch)
                        graph.translate(0.0001, 0.0001);
                        paper.unfreeze();

                        if (activeTopNode === element) {
                            service.activeTopNode = null;
                            canvasActions.resizePaper(paper);
                            buttonView?.update();
                            return;
                        }
                    }
                    service.activeTopNode = element;
                    const [faultTreeNode] = graph.getNeighbors(element).filter((cell) => cell.get('nodeClass') === 'FAULTTREENODE');
                    if (!faultTreeNode) {
                        let viewportOverlap = 50;
                        let viewportRect: any = paperScroller.getVisibleArea().inflate(viewportOverlap);
                        // Set collapsed: false immediately to avoid race condition with setTimeout
                        element.set({ collapsed: false });
                        if (service.isRestoringState) {
                            // During state restore, run synchronously (no spinner needed)
                            canvasActions.createFtGraphAndExpand(service, element, graph, paper, paperScroller, viewportRect);
                            service.drawDnaPath();
                        } else {
                            // Show loading spinner while FT is being created
                            service.setLoading(true);
                            const timeoutId = setTimeout(() => {
                                // Clear the timeout tracking since we're now executing
                                service.setPendingExpansionTimeout(null);
                                canvasActions.createFtGraphAndExpand(service, element, graph, paper, paperScroller, viewportRect);
                                service.drawDnaPath();
                            }, 0); // allow for the loading spinner to render
                            // Store timeout ID so it can be cancelled if canvas is destroyed
                            service.setPendingExpansionTimeout(timeoutId);
                        }
                        return;
                    }
                }
                let viewportOverlap = 50;
                let viewportRect: any = paperScroller.getVisibleArea().inflate(viewportOverlap);

                // Get affected elements BEFORE toggle (branch being expanded/collapsed)
                const isFaultTreeNode = element.get('nodeClass') === 'FAULTTREENODE';

                // Handle lazy-loaded nodes: load children on demand instead of toggling
                if (isFaultTreeNode && element.get('hasUnloadedChildren')) {
                    canvasActions.expandLazyNode(service, element, graph, paper, paperScroller, viewportRect);
                    service.drawDnaPath();
                    return;
                }

                let affectedElements: joint.dia.Element[] = [];
                if (isFaultTreeNode) {
                    // Get the branch that will be affected by toggle
                    affectedElements = graph.getSuccessors(element).filter(
                        (cell): cell is joint.dia.Element => cell.isElement()
                    );
                }

                // Remove callouts/PSFs only from affected branch (not entire graph) - single pass
                if (isFaultTreeNode && (service.isCalloutsModeActive || service.isOdnaModeActive)) {
                    for (const el of affectedElements) {
                        const nodeClass = el.get('nodeClass');
                        if (service.isCalloutsModeActive && nodeClass === 'CALLOUTNODE') {
                            el.remove();
                        } else if (service.isOdnaModeActive && nodeClass === 'PSFNODE') {
                            service.psfNodeIds.delete(el.id as string);
                            el.remove();
                        }
                    }
                }

                canvasActions.toggleBranch(service, element, graph, paper, paperScroller, viewportRect);

                // Re-add callouts/PSFs only to visible elements (not entire graph)
                if (isFaultTreeNode && service.isCalloutsModeActive) {
                    const visibleElements = canvasActions.filterVisibleElements(paperScroller, graph.getElements());
                    for (const el of visibleElements) {
                        if (el.get('nodeClass') !== 'CALLOUTNODE' && el.get('nodeClass') !== 'PSFNODE') {
                            setCalloutOpenForm(graph, paper, el);
                        }
                    }
                }
                if (isFaultTreeNode && service.isOdnaModeActive) {
                    canvasActions.drawAllPsfs(service);
                }

                service.drawDnaPath();
            }

        })
    }
}
